import { useState, useCallback } from "react"
import { Activity, AlertTriangle, RefreshCw, Search, ShieldCheck, ShieldOff } from "lucide-react"
import {
  fetchDiagnosisDigest,
  getDiagnosisSteering,
  setDiagnosisSteering,
  clearDiagnosisSteering,
  type DiagnosisDigest,
  type DiagnosisRow,
  type SteeringState,
} from "@/lib/api"
import { cn } from "@/lib/utils"

const POSTURES = ["progress", "hold", "deload", "rebuild", "taper", "build-foundation"]

function short(id: string | null): string {
  return id ? id.slice(0, 8) : "—"
}

function fmt(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

const POSTURE_TINT: Record<string, string> = {
  progress: "bg-green-100 text-green-700",
  hold: "bg-slate-100 text-slate-700",
  deload: "bg-amber-100 text-amber-700",
  rebuild: "bg-blue-100 text-blue-700",
  taper: "bg-purple-100 text-purple-700",
  "build-foundation": "bg-teal-100 text-teal-700",
}

export function DiagnosisDigestPage() {
  const [userId, setUserId] = useState("")
  const [start, setStart] = useState("")
  const [end, setEnd] = useState("")
  const [posture, setPosture] = useState("")
  const [digest, setDigest] = useState<DiagnosisDigest | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [steering, setSteering] = useState<SteeringState | null>(null)
  const [steeringBusy, setSteeringBusy] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await fetchDiagnosisDigest({
        user_id: userId.trim() || undefined,
        start: start || undefined,
        end: end || undefined,
        posture: posture || undefined,
      })
      setDigest(data)
      // Surface the per-user steering knob (Story 3.5) only when scoped to one user.
      const uid = userId.trim()
      if (uid) {
        setSteering(await getDiagnosisSteering(uid).catch(() => null))
      } else {
        setSteering(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load digest")
      setDigest(null)
      // F3B (gemini): don't leave a prior user's steering panel visible after a failed query.
      setSteering(null)
    } finally {
      setIsLoading(false)
    }
  }, [userId, start, end, posture])

  const toggleSteering = async (enabled: boolean) => {
    const uid = userId.trim()
    if (!uid) return
    setSteeringBusy(true)
    // F3B-2: don't leave a stale error banner on a successful retry.
    setError(null)
    try {
      setSteering(await setDiagnosisSteering(uid, enabled))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update steering")
    } finally {
      setSteeringBusy(false)
    }
  }

  const resetSteering = async () => {
    const uid = userId.trim()
    if (!uid) return
    setSteeringBusy(true)
    setError(null)  // F3B-2
    try {
      setSteering(await clearDiagnosisSteering(uid))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear steering")
    } finally {
      setSteeringBusy(false)
    }
  }

  // F3B-3 (wrong-user flip guard): lock steering controls when the User ID input
  // drifts from the loaded row — prevents flipping a different user than the one
  // on screen.
  const steeringTargetMatches = !!steering && steering.user_id === userId.trim()

  // The set of diagnosis-row ids that are the "current" side of a violation —
  // flag them inline in the table (UX-DR8).
  const violationIds = new Set((digest?.invariant_violations ?? []).map((v) => v.curr_id))

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Activity className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-bold">Diagnosis Digest</h1>
        <span className="text-sm text-muted-foreground">shadow-mode posture log · flip readiness</span>
      </div>

      <div className="rounded-lg border bg-card p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          User ID
          <input
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="uuid (optional)"
            className="w-72 rounded-md border px-2 py-1.5 text-sm text-foreground"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Start
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm text-foreground" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          End
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm text-foreground" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Posture
          <select value={posture} onChange={(e) => setPosture(e.target.value)}
            className="rounded-md border px-2 py-1.5 text-sm text-foreground">
            <option value="">all</option>
            {POSTURES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <button
          onClick={load}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-accent disabled:opacity-50"
        >
          {isLoading ? <RefreshCw className="size-3.5 animate-spin" /> : <Search className="size-3.5" />}
          Query
        </button>
      </div>

      {/* Per-user steering knob (Story 3.5) — only when scoped to one user */}
      {steering && (
        <div className="rounded-lg border bg-card p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {steering.steering_active
                ? <ShieldCheck className="size-5 text-green-600" />
                : <ShieldOff className="size-5 text-muted-foreground" />}
              <span className="font-medium">Diagnosis steering:</span>
              <span className={cn("font-semibold", steering.steering_active ? "text-green-700" : "text-muted-foreground")}>
                {steering.steering_active ? "ACTIVE (live)" : "shadow (off)"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleSteering(true)} disabled={steeringBusy || !steeringTargetMatches}
                className="px-2.5 py-1 text-xs rounded-md border hover:bg-accent disabled:opacity-50">Enable</button>
              <button onClick={() => toggleSteering(false)} disabled={steeringBusy || !steeringTargetMatches}
                className="px-2.5 py-1 text-xs rounded-md border hover:bg-accent disabled:opacity-50">Disable</button>
              <button onClick={resetSteering} disabled={steeringBusy || !steeringTargetMatches}
                className="px-2.5 py-1 text-xs rounded-md border hover:bg-accent disabled:opacity-50">Clear override</button>
            </div>
          </div>
          {/* F3B-4 / F3B-7: confirm-before-flip transparency — full target id +
              the RAW stored override (not just the resolved value), so a
              malformed override can't silently mislead the flip decision. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>target: <span className="font-mono text-foreground">{steering.user_id}</span></span>
            <span>raw override: <span className="font-mono text-foreground">{String(steering.override)}</span></span>
            <span>source: {steering.source}</span>
            <span>env default: {String(steering.env_default)}</span>
          </div>
          {!steeringTargetMatches && (
            <p className="text-xs text-amber-600">
              Controls locked — the User ID was edited. Re-run Query to load this user's state before flipping.
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive flex items-center gap-2">
          <AlertTriangle className="size-4" /> {error}
        </div>
      )}

      {digest && (
        <>
          <div className="flex flex-wrap gap-3 text-sm">
            <Chip label="Rows" value={digest.count} />
            <Chip label="Violations" value={digest.invariant_violations.length}
              tone={digest.invariant_violations.length > 0 ? "bad" : "good"} />
            <Chip label="Churn weeks" value={digest.posture_churn.length}
              tone={digest.posture_churn.length > 0 ? "warn" : "good"} />
            <Chip label="Range" value={`${digest.range.start ?? "—"} → ${digest.range.end ?? "—"}`} />
          </div>

          {digest.truncated && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
              Result capped at the row limit — narrow the date range or filter by user/posture for a complete view.
              The SQL view <code>diagnosis_posture_invariant_violations</code> stays exhaustive.
            </div>
          )}

          <div className="rounded-lg border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Generated</th>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Posture</th>
                  <th className="px-3 py-2 font-medium">Conf.</th>
                  <th className="px-3 py-2 font-medium">Steering</th>
                  <th className="px-3 py-2 font-medium">Outcome</th>
                  <th className="px-3 py-2 font-medium">Cited inputs</th>
                </tr>
              </thead>
              <tbody>
                {digest.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No diagnoses in range.</td></tr>
                )}
                {digest.rows.map((row) => <Row key={row.id} row={row} flagged={violationIds.has(row.id)} />)}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!digest && !isLoading && !error && (
        <p className="text-sm text-muted-foreground">Set filters and hit Query to read the shadow-mode diagnosis log.</p>
      )}
    </div>
  )
}

function Chip({ label, value, tone }: { label: string; value: string | number; tone?: "good" | "bad" | "warn" }) {
  return (
    <div className={cn(
      "rounded-lg border px-3 py-2",
      tone === "bad" && "border-red-200 bg-red-50/50",
      tone === "warn" && "border-amber-200 bg-amber-50/50",
      tone === "good" && "border-green-200 bg-green-50/50",
    )}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="font-semibold">{value}</div>
    </div>
  )
}

function Row({ row, flagged }: { row: DiagnosisRow; flagged: boolean }) {
  return (
    <tr className={cn("border-t", flagged && "bg-red-50/60")}>
      <td className="px-3 py-2 whitespace-nowrap">{fmt(row.generated_at)}</td>
      <td className="px-3 py-2 font-mono text-xs">{short(row.user_id)}</td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          {row.posture ? (
            <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", POSTURE_TINT[row.posture] ?? "bg-slate-100 text-slate-700")}>
              {row.posture}
            </span>
          ) : <span className="text-xs text-muted-foreground">none</span>}
          {flagged && (
            <span title="Invariant violation: posture changed with identical cited inputs"
              className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
              <AlertTriangle className="size-3" /> violation
            </span>
          )}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">{row.confidence ?? "—"}</td>
      <td className="px-3 py-2">
        <span className={cn("text-xs font-medium", row.steering_active ? "text-green-700" : "text-muted-foreground")}>
          {row.steering_active ? "live" : "shadow"}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">{row.generation_outcome}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground max-w-md">
        <span className="font-mono wrap-break-word">{row.cited_inputs?.join("  ·  ") || "—"}</span>
      </td>
    </tr>
  )
}
